import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { PdfService } from '../../../../services/pdf.service';
import { ClaimsService } from '../../../../services/claims.service';
import { LocaleService } from '../../../../locale/locale.service';

@Component({
  selector: 'app-pdf-generator',
  imports: [CommonModule],
  template: `
    <div class="pdf-generator-container">
      <h2>PDF Generator</h2>
      <p class="description">Generate PDF reports based on the overview component data.</p>

      <div class="status-section">
        <h3>Claim Data Status</h3>
        <div class="status-item">
          <span class="label">Claim Report Available:</span>
          <span class="value" [class.available]="hasClaimData" [class.unavailable]="!hasClaimData">
            {{ hasClaimData ? 'Yes' : 'No' }}
          </span>
        </div>
        <div class="status-item" *ngIf="hasClaimData">
          <span class="label">License Plate:</span>
          <span class="value">{{ claimsService.claimReport().licensePlate || 'N/A' }}</span>
        </div>
        <div class="status-item" *ngIf="hasClaimData">
          <span class="label">Incident Date:</span>
          <span class="value">{{ formatDate(claimsService.claimReport().incidentDate) }}</span>
        </div>
      </div>

      <div class="actions-section">
        <h3>PDF Generation Options</h3>
        <div class="button-group">
          <button
            class="pdf-button primary"
            [disabled]="!hasClaimData || isGenerating"
            (click)="generateDataBasedPDF()">
            {{ isGenerating ? 'Generating...' : 'Generate Data-Based PDF' }}
          </button>

          <button
            class="pdf-button secondary"
            [disabled]="isGenerating"
            (click)="generateMockPDF()">
            {{ isGenerating ? 'Generating...' : 'Generate Mock Data PDF' }}
          </button>
        </div>

        <div class="info-box">
          <h4>Generation Methods:</h4>
          <ul>
            <li><strong>Data-Based PDF:</strong> Uses actual claim data from the service</li>
            <li><strong>Mock Data PDF:</strong> Uses sample data for testing purposes</li>
          </ul>
        </div>
      </div>

      <div class="preview-section" *ngIf="hasClaimData">
        <h3>Data Preview</h3>
        <div class="preview-grid">
          <div class="preview-item">
            <span class="preview-label">User Type:</span>
            <span class="preview-value">{{ getUserTypeDisplay() }}</span>
          </div>
          <div class="preview-item">
            <span class="preview-label">Incident Types:</span>
            <span class="preview-value">{{ getIncidentTypesDisplay() }}</span>
          </div>
          <div class="preview-item">
            <span class="preview-label">Personal Info:</span>
            <span class="preview-value">{{ getPersonalInfoDisplay() }}</span>
          </div>
          <div class="preview-item">
            <span class="preview-label">Witnesses:</span>
            <span class="preview-value">{{ getWitnessesDisplay() }}</span>
          </div>
        </div>
      </div>

      <div class="error-section" *ngIf="errorMessage">
        <div class="error-message">
          <strong>Error:</strong> {{ errorMessage }}
        </div>
      </div>
    </div>
  `,
  styles: [`
    .pdf-generator-container {
      padding: 20px;
      max-width: 800px;
    }

    h2 {
      color: #333;
      margin-bottom: 10px;
    }

    .description {
      color: #666;
      margin-bottom: 30px;
    }

    .status-section, .actions-section, .preview-section {
      background: #f8f9fa;
      border: 1px solid #dee2e6;
      border-radius: 8px;
      padding: 20px;
      margin-bottom: 20px;
    }

    h3 {
      color: #495057;
      margin-top: 0;
      margin-bottom: 15px;
    }

    .status-item {
      display: flex;
      margin-bottom: 8px;
    }

    .label {
      font-weight: 600;
      min-width: 150px;
      color: #6c757d;
    }

    .value {
      color: #495057;
    }

    .value.available {
      color: #28a745;
      font-weight: 600;
    }

    .value.unavailable {
      color: #dc3545;
      font-weight: 600;
    }

    .button-group {
      display: flex;
      gap: 15px;
      margin-bottom: 20px;
    }

    .pdf-button {
      padding: 12px 24px;
      border: none;
      border-radius: 6px;
      font-size: 14px;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.2s ease;
    }

    .pdf-button:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    .pdf-button.primary {
      background-color: #007bff;
      color: white;
    }

    .pdf-button.primary:hover:not(:disabled) {
      background-color: #0056b3;
    }

    .pdf-button.secondary {
      background-color: #6c757d;
      color: white;
    }

    .pdf-button.secondary:hover:not(:disabled) {
      background-color: #545b62;
    }

    .info-box {
      background: #e9ecef;
      border-left: 4px solid #007bff;
      padding: 15px;
      border-radius: 4px;
    }

    .info-box h4 {
      margin-top: 0;
      margin-bottom: 10px;
      color: #495057;
    }

    .info-box ul {
      margin: 0;
      padding-left: 20px;
    }

    .info-box li {
      margin-bottom: 5px;
      color: #6c757d;
    }

    .preview-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
      gap: 15px;
    }

    .preview-item {
      display: flex;
      flex-direction: column;
      gap: 5px;
    }

    .preview-label {
      font-weight: 600;
      color: #6c757d;
      font-size: 12px;
      text-transform: uppercase;
    }

    .preview-value {
      color: #495057;
      background: white;
      padding: 8px;
      border-radius: 4px;
      border: 1px solid #dee2e6;
    }

    .error-section {
      background: #f8d7da;
      border: 1px solid #f5c6cb;
      border-radius: 8px;
      padding: 15px;
    }

    .error-message {
      color: #721c24;
    }
  `]
})
export class PdfGeneratorComponent implements OnInit {

  private pdfService = inject(PdfService);
  claimsService = inject(ClaimsService);
  private localeService = inject(LocaleService);

  isGenerating = false;

  errorMessage = '';

  ngOnInit(): void {
    this.claimsService.getData().subscribe();
  }

  get hasClaimData(): boolean {
    return !!this.claimsService.claimReport();
  }

  async generateDataBasedPDF(): Promise<void> {
    if (!this.hasClaimData) {
      this.errorMessage = 'No claim data available. Please ensure claim data is loaded.';
      return;
    }

    this.isGenerating = true;
    this.errorMessage = '';

    try {
      await this.pdfService.generateOverviewPDF();
    } catch (error) {
      this.errorMessage = `Failed to generate PDF: ${error instanceof Error ? error.message : 'Unknown error'}`;
    } finally {
      this.isGenerating = false;
    }
  }

  async generateMockPDF(): Promise<void> {
    this.isGenerating = true;
    this.errorMessage = '';

    try {
      // Create temporary mock data for testing
      this.createMockData();
      await this.pdfService.generateOverviewPDF();
    } catch (error) {
      this.errorMessage = `Failed to generate mock PDF: ${error instanceof Error ? error.message : 'Unknown error'}`;
    } finally {
      this.isGenerating = false;
    }
  }

  private createMockData(): void {
    // This would typically be done through the ClaimsService
    // For demo purposes, we're showing what mock data structure would look like
    console.log('Mock data would be created here for PDF generation testing');
  }

  getUserTypeDisplay(): string {
    const userType = this.claimsService.claimReport()?.userType;
    return userType ? `${userType}` : 'N/A';
  }

  getIncidentTypesDisplay(): string {
    const incidentTypes = this.claimsService.claimReport()?.incidentTypes;
    return incidentTypes && incidentTypes.length > 0
      ? `${incidentTypes.length} type(s) selected`
      : 'None';
  }

  getPersonalInfoDisplay(): string {
    const personalInfo = this.claimsService.claimReport()?.personalInformation;
    return personalInfo
      ? `${personalInfo.name || ''} ${personalInfo.surname || ''}`.trim() || 'Available'
      : 'Not available';
  }

  getWitnessesDisplay(): string {
    const witnesses = this.claimsService.claimReport()?.witnesses;
    return witnesses && witnesses.length > 0
      ? `${witnesses.length} witness(es)`
      : 'None';
  }

  formatDate(date: any): string {
    if (!date) return 'N/A';

    try {
      const d = new Date(date);
      return d.toLocaleString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'Invalid Date';
    }
  }
}
